import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { GoogleLogo } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

export default function Signup() {
  const { signup } = useAuth();
  const [form, setForm] = useState({ full_name: "", organization_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(extractApiError(err, "Signup failed"));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background" data-testid="signup-page">
      <div className="flex items-center justify-center p-6 sm:p-8 lg:p-16 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <Logo variant="wordmark" size={36} to="/" testId="signup-logo" />
          </div>
          <div className="text-overline text-primary mb-3">Create account</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">Your intelligence workspace.</h1>

          <button onClick={googleLogin} className="w-full border border-border bg-card rounded-lg py-3 flex items-center justify-center gap-3 hover:bg-secondary transition-colors mb-6 font-medium" data-testid="signup-google">
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6 text-xs text-muted-foreground font-semibold uppercase tracking-wider">
            <div className="flex-1 h-px bg-border" /> OR <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-overline text-muted-foreground block mb-2">Full name</label>
              <input required value={form.full_name} onChange={set("full_name")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" data-testid="signup-name" />
            </div>
            <div>
              <label className="text-overline text-muted-foreground block mb-2">Organisation</label>
              <input required value={form.organization_name} onChange={set("organization_name")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" data-testid="signup-org" />
            </div>
            <div>
              <label className="text-overline text-muted-foreground block mb-2">Email</label>
              <input type="email" required value={form.email} onChange={set("email")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" data-testid="signup-email" />
            </div>
            <div>
              <label className="text-overline text-muted-foreground block mb-2">Password (min 8 chars)</label>
              <input type="password" required minLength={8} value={form.password} onChange={set("password")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" data-testid="signup-password" />
            </div>
            <button disabled={loading} type="submit" className="w-full bg-primary text-primary-foreground rounded-lg py-3 font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 card-elev" data-testid="signup-submit">
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>

          <p className="mt-5 text-xs text-muted-foreground leading-relaxed">
            By creating an account, you agree to our{" "}
            <Link to="/legal/terms" className="text-primary underline underline-offset-4" data-testid="signup-terms-link">Terms of Service</Link>,{" "}
            <Link to="/legal/privacy" className="text-primary underline underline-offset-4" data-testid="signup-privacy-link">Privacy Policy</Link> and{" "}
            <Link to="/legal/refund" className="text-primary underline underline-offset-4" data-testid="signup-refund-link">Refund Policy</Link>.
          </p>

          <div className="mt-6 text-sm text-muted-foreground">
            Already have one? <Link to="/login" className="text-primary underline underline-offset-4 font-semibold" data-testid="signup-to-login">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative africa-bg border-l border-border order-1 lg:order-2">
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--info))]/10 via-transparent to-primary/10" />
        <div className="relative h-full flex flex-col justify-between p-12 max-w-md ml-auto">
          <div className="text-overline text-primary text-right">Enterprise</div>
          <div>
            <div className="font-display text-3xl xl:text-4xl leading-tight">
              Built for institutions deploying capital across Africa.
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 text-xs">
              <div><div className="font-display text-2xl text-primary">PE</div><div className="text-muted-foreground">Funds</div></div>
              <div><div className="font-display text-2xl text-primary">DFI</div><div className="text-muted-foreground">Mandates</div></div>
              <div><div className="font-display text-2xl text-primary">FO</div><div className="text-muted-foreground">Family Offices</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
